import React, { forwardRef, RefObject, createRef, Key, PropsWithChildren, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import classnames from 'classnames';
import { InfoMessage } from './formElements';
import colors from '../../common/theme/colors';
import constants from '../../common/theme/constants';
import { FormValue, KitInputProps } from '../../common/types';
import { StyledLegend } from './KitFormControls';

const unselectedRadioColor = colors.gray_30;
const selectedRadioColor = colors.orange_50;

interface FormItemProps extends KitInputProps {
  type?: string;
  selected?: boolean;
}

interface FormItemLabel {
  htmlFor: string;
  selected?: boolean;
  disabled?: boolean;
}

const StyledRadioWrapper = styled.div<Partial<FormItemProps>>`
  font-family: ${({ theme }) => theme.font.fontFamily};
  align-items: center;
  align-content: center;
  cursor: pointer;
  display: flex;
  flex-flow: row wrap;
  padding: ${constants.spacing.absolute.xs}px;
  position: relative;
  transition: box-shadow 0.6s;

  &.disabled {
    cursor: not-allowed;
  }

  .cp-form-label--description {
    margin-bottom: ${constants.spacing.s}rem;
    color: ${({ theme, disabled }) => (disabled ? colors.gray_30 : theme.checkbox.label)};
    opacity: 0.7;
  }
}`;

const StyledRadio = styled.input<{ [key: string]: unknown }>`
  font-family: ${({ theme }) => theme.font.fontFamily};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  position: absolute;
  content: ' ';
  height: ${constants.spacing.absolute.l}px;
  width: ${constants.spacing.absolute.l}px;
  opacity: 0;
  &:focus + label {
    &:before {
      box-shadow: 0 0 0 ${constants.border.width}px ${({ theme }) => theme.focus};
    }
  }
`;

const StyledLabel = styled.label<FormItemLabel>`
  font-family: ${({ theme }) => theme.font.fontFamily};
  color: ${({ theme, disabled }) => (disabled ? colors.gray_30 : theme.checkbox.label)};
  font-size: ${constants.fontSize.text_14}rem;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: block;
  position: relative;
  width: 100%;
  margin-bottom: 0 !important;

  * {
    margin-left: ${constants.spacing.l}rem;
  }

  &:before {
    transition: 0.3s;
    position: absolute;
    border: 1px solid
      ${({ selected, disabled }) =>
        selected && !disabled ? selectedRadioColor : unselectedRadioColor};
    border-radius: 100%;
    content: ' ';
    left: 0;
    top: 0;
    width: ${constants.fontSize.base}rem;
    height: ${constants.fontSize.base}rem;
  }

  &:after {
    background: ${({ disabled }) => (disabled ? colors.gray_30 : selectedRadioColor)};
    border: 0;
    border-radius: 100%;
    content: ' ';
    left: 0.1rem;
    top: 0.1rem;
    opacity: ${(props) => (props.selected ? 1 : 0)};
    position: absolute;
    transition: 0.3s;
    width: 0.8rem;
    height: 0.8rem;
    transform: scale(${(props) => (props.selected ? 1 : 0)});
  }
`;

const StyledKitRadioGroup = styled.fieldset<{ error?: string; disabled?: boolean }>`
  padding: ${constants.spacing.absolute.s}px;
  font-family: ${({ theme }) => theme.font.fontFamily};
  width: fit-content;
  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      label {
        cursor: not-allowed;
        color: ${colors.gray_50};
      }
    `}
`;

export interface KitRadioProps extends KitInputProps {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  tabIndex?: number;
  onChange?: (e: MouseEvent | unknown) => void;
  children?: ReactNode;
  description?: string;
}

const KitRadio = forwardRef((props: KitRadioProps, ref: RefObject<HTMLInputElement>) => {
  const { label, description, children, onChange, className, disabled, ...inputProps } = props;
  const inputId = `${props.id ?? [props.name ?? props.label, props.value].join('-')}`;
  const handleChange = (e: MouseEvent) => {
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <StyledRadioWrapper selected={inputProps.checked} className={classnames(disabled, className)}>
      <StyledRadio
        disabled={disabled}
        id={inputId}
        ref={ref}
        tabIndex={inputProps.tabIndex ?? 0}
        type='radio'
        onChange={(e) => handleChange(e)}
        {...inputProps}
      />
      <StyledLabel
        selected={inputProps.checked ?? inputProps.defaultChecked}
        className='cp-form-label'
        disabled={disabled}
        htmlFor={inputId}
      >
        {label && <span>{label}</span>}
        {description && <div className='cp-form-label--description'>{description}</div>}
        {children}
      </StyledLabel>
    </StyledRadioWrapper>
  );
});

interface OptionItemInterface {
  id?: string;
  name?: string;
  label: string;
  value: FormValue;
}

export interface KitRadioGroupProps extends PropsWithChildren {
  disabled?: boolean;
  defaultValue?: FormValue;
  error?: string;
  helperText?: string;
  label?: string;
  name?: string;
  onChange?: (e: MouseEvent | unknown) => void;
  options?: OptionItemInterface[];
  required?: boolean;
  value?: FormValue;
}

/**
 * KitRadioGroup - wrapper around KitRadio buttons to treat them as a single control
 * You can either pass options as
 * - children
 * OR
 * - array of objects that have required properties (name, value, label, etc)
 * @param param0
 */
export const KitRadioGroup: React.FC<KitRadioGroupProps> = ({
  name,
  children,
  disabled,
  defaultValue,
  helperText,
  error,
  label,
  options,
  required,
  onChange,
  value,
}) => {
  const optRefs: RefObject<HTMLInputElement>[] = options ? options.map(() => createRef()) : [];

  const getOptionProps = (opt) => {
    const props: Partial<Pick<HTMLInputElement, 'checked' | 'disabled' | 'defaultChecked'>> = {};
    if ([value].includes(opt.value)) {
      props.checked = true;
    }
    if (defaultValue === opt.value) {
      props.defaultChecked = true;
    }
    if (disabled) {
      props.disabled = true;
    }
    return props;
  };

  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <StyledKitRadioGroup
      name={name}
      error={error}
      disabled={disabled}
      aria-required={required}
      aria-invalid={!!error}
      aria-label={error ? `${label}, ${error}` : label}
    >
      {label && (
        <StyledLegend error={error} required={required}>
          {label}
        </StyledLegend>
      )}
      {children}
      {options &&
        options.map((opt, i) => (
          <KitRadio
            key={opt.value as Key}
            ref={optRefs.length ? optRefs[i] : null}
            id={`${opt.id ?? [name, opt.label, i].join('-')}`}
            {...getOptionProps(opt)}
            name={name ?? opt.name}
            onChange={handleChange}
            {...opt}
          />
        ))}
      {(helperText || error) && <InfoMessage error={error}>{error || helperText}</InfoMessage>}
    </StyledKitRadioGroup>
  );
};

export default KitRadio;
